import type { ReactNode } from 'react';
import { Button, Icon, Select, cn } from '@forecastify/ui';
import { useT } from '../i18n';
import { SELECT_SKIN } from './tones';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `[<] [1. oldal ▾] [>] 14 szervezet` — CLAUDE.md's settled pager.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP'S `FCC.Pager`, AND IT IS EXACTLY THE SHAPE CLAUDE.md SETTLED** — *"`[<]
 * [selector] [>] [total]` on every list, offset-based, with a page jump and a true total."* The
 * product's `ListPager` has since grown words at the ends and page numbers in the middle; the
 * console's drawing is the settled four parts, so it gets them rather than the product's variant.
 *
 * ⚠️ **The selector's options carry `/ n` as a hint**, so opening it says how far the list goes.
 */
export function Pager({
  page,
  pageCount,
  total,
  onChange,
}: {
  /** Zero-based. */
  readonly page: number;
  readonly pageCount: number;
  /** Already worded — *14 szervezet*. */
  readonly total: string;
  readonly onChange: (page: number) => void;
}): ReactNode {
  const t = useT();
  const count = Math.max(1, pageCount);
  const options = Array.from({ length: count }, (_, index) => ({
    value: String(index),
    label: t('pager.page', { n: index + 1 }),
    hint: `/ ${String(count)}`,
  }));

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={page <= 0}
        aria-label={t('pager.previous')}
        onClick={() => {
          onChange(page - 1);
        }}
      >
        <Icon name="chevron-left" size={14} />
      </Button>
      <div className="w-[118px]">
        <Select
          aria-label={t('pager.jump')}
          options={options}
          value={String(page)}
          onChange={(event) => {
            onChange(Number(event.target.value));
          }}
          className={cn(SELECT_SKIN, 'h-7 px-2 text-[12.5px]')}
        />
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={page >= count - 1}
        aria-label={t('pager.next')}
        onClick={() => {
          onChange(page + 1);
        }}
      >
        <Icon name="chevron-right" size={14} />
      </Button>
      <span className="text-muted-foreground ml-2 text-[12px] tabular-nums">{total}</span>
    </div>
  );
}
